import type { MouseEvent as ReactMouseEvent } from 'react';

import { useBehavioralTrackingProps as _useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import type { ChartToolbarRendererProps } from '../../../core/components/toolbar/types.js';
import { getMenuTriggerDisplayMode } from '../../../core/components/toolbar/utils/get-menu-trigger-display-mode.js';
import { getMenuTriggerIcon } from '../../../core/components/toolbar/utils/get-menu-trigger-icon.js';
import { ChartOverlaysElevation } from '../../../core/constants.js';
import type { ToolbarPlacement } from '../../../core/types/toolbar.js';
import { getA11yToolbarConfig } from '../../../core/utils/zoom.js';
import { defaultToolbar } from '../../constants/defaults.js';
import { useDownload } from '../../hooks/useDownload.js';
import { useToolbar } from '../../hooks/useToolbar.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { useXYChartSelectionAreaRef } from '../../hooks/useXYChartSelectionAreaRef.js';
import { useInteractionModeState } from '../../store/selectors.js';
import { buildAndDownloadCsv } from '../../utils/build-and-download-csv.js';
import { DownloadDataButton } from './DownloadDataButton.js';
import { ExploreButton } from './ExploreButton.js';
import { PanButton } from './PanButton.js';
import { ResetButton } from './ResetButton.js';
import { ZoomInOutButtons } from './ZoomInOutButtons.js';
import { ZoomModeButton } from './ZoomModeButton.js';

type XYChartToolbarProps = {
  onMouseEnter?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave?: (react?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  handlerRef?: ChartToolbarRendererProps['handlerRef'];
};

export const XYChartToolbar = ({
  onMouseEnter,
  onMouseLeave,
  handlerRef,
}: XYChartToolbarProps) => {
  const toolbarContext = useToolbar();
  const { collapsed, placement, hidden, intents } = toolbarContext;
  const { downloadDataCallback } = useDownload();
  const interactionMode = useInteractionModeState();
  const { axesMetadata } = useXYChartData();
  const selectionAreaRef = useXYChartSelectionAreaRef();

  const isZoomEnabled = axesMetadata.isZoomEnabled;
  const isPanEnabled = axesMetadata.isPanEnabled;
  const shouldHideInteractionControls = !isZoomEnabled && !isPanEnabled;
  const exploreBtnPlace = isZoomEnabled || isPanEnabled ? 'menu' : 'auto';
  const isDownloadEnabled = true;

  const menuDisplayMode = shouldHideInteractionControls
    ? 'never'
    : getMenuTriggerDisplayMode({
        isPanEnabled,
        isZoomEnabled,
        isDownloadEnabled,
      });

  const showDefaultToolbarDownloadButton = isDownloadEnabled && !intents;
  const showMenuToolbarButton = isDownloadEnabled && !!intents?.length;

  const startsExpanded = !collapsed;
  const hasExploreMenu =
    menuDisplayMode === 'always' ||
    (menuDisplayMode === 'expanded' && startsExpanded) ||
    (menuDisplayMode === 'collapsed' && !startsExpanded);

  const initialA11yState = getA11yToolbarConfig({
    zoom: { enabled: isZoomEnabled },
    pan: { enabled: isPanEnabled },
    download: { enabled: showDefaultToolbarDownloadButton },
    intent: !!intents,
    hasExploreMenu,
  });

  const menuIcon = getMenuTriggerIcon(interactionMode, isZoomEnabled);

  const { seriesWithDatapoints, datapointMetadataMap, seriesToMetadata } =
    useXYChartData();

  const handleDownloadDataToolbar = () => {
    buildAndDownloadCsv(
      seriesWithDatapoints,
      seriesToMetadata,
      datapointMetadataMap,
      downloadDataCallback,
    );
  };

  const onClose = () =>
    selectionAreaRef ? selectionAreaRef.current?.focus() : undefined;

  const behaviorTrackingProps = _useBehavioralTrackingProps(toolbarContext)[0];

  return !hidden ? (
    <ChartToolbarRenderer
      visibleWhenMouseInBounds
      handlerRef={handlerRef}
      placement={placement ?? (defaultToolbar.placement as ToolbarPlacement)}
      draggable={true}
      removeDragHandle={shouldHideInteractionControls}
      collapsed={collapsed}
      initialA11yState={initialA11yState}
      zIndex={ChartOverlaysElevation.toolbar}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      menuIcon={menuIcon}
      showMenuDivider={!shouldHideInteractionControls}
      menuDisplayMode={menuDisplayMode}
      isDownloadEnabled={showMenuToolbarButton}
      intents={intents}
      handleDownloadDataToolbar={handleDownloadDataToolbar}
      onClose={onClose}
      {...behaviorTrackingProps}
    >
      <>
        {!shouldHideInteractionControls && (
          <ExploreButton placement={exploreBtnPlace} />
        )}
        {isZoomEnabled && <ZoomModeButton />}
        {isPanEnabled && <PanButton />}
      </>
      {isZoomEnabled && <ZoomInOutButtons />}
      {isZoomEnabled && <ResetButton />}
      {showDefaultToolbarDownloadButton && (
        <DownloadDataButton
          divider={shouldHideInteractionControls ? undefined : 'left'}
        />
      )}
    </ChartToolbarRenderer>
  ) : null;
};

XYChartToolbar['displayName'] = 'XYChartToolbar';
